import { toApiError } from "./classifyFailure";
import type { Backend, Credentials, ServerSettings, TaskApi } from "./taskApi";
import { WasmTaskApi, type WasmClient } from "./wasmTaskApi";

export const SERVER_STORAGE_KEY = "td.server";
export const PASSWORD_SESSION_KEY = "td.session-password";

export type WasmConnector = (credentials: Credentials) => Promise<WasmClient>;

interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The server URL and username persist in `localStorage`. The password lives only in
 * `sessionStorage`, so a reload of the same tab resumes and a new tab asks again.
 */
export class WebBackend implements Backend {
  readonly kind = "web";

  constructor(
    private readonly connect: WasmConnector,
    private readonly persistent: KeyValueStore = window.localStorage,
    private readonly perTab: KeyValueStore = window.sessionStorage,
  ) {}

  savedServer(): Promise<ServerSettings | undefined> {
    return Promise.resolve(this.readServer());
  }

  async resume(): Promise<TaskApi | undefined> {
    const server = this.readServer();
    const password = this.perTab.getItem(PASSWORD_SESSION_KEY);
    if (server === undefined || password === null) return undefined;
    return this.signIn({ ...server, password }).catch(() => undefined);
  }

  async signIn(credentials: Credentials): Promise<TaskApi> {
    const api = new WasmTaskApi(await this.connect(credentials));
    try {
      await api.sync();
    } catch (failure) {
      throw toApiError(failure);
    }
    const { serverUrl, username } = credentials;
    this.persistent.setItem(SERVER_STORAGE_KEY, JSON.stringify({ serverUrl, username }));
    this.perTab.setItem(PASSWORD_SESSION_KEY, credentials.password);
    return api;
  }

  signOut(): Promise<void> {
    this.perTab.removeItem(PASSWORD_SESSION_KEY);
    return Promise.resolve();
  }

  private readServer(): ServerSettings | undefined {
    const stored = this.persistent.getItem(SERVER_STORAGE_KEY);
    if (stored === null) return undefined;
    try {
      const parsed = JSON.parse(stored) as Partial<ServerSettings>;
      if (typeof parsed.serverUrl === "string" && typeof parsed.username === "string") {
        return { serverUrl: parsed.serverUrl, username: parsed.username };
      }
    } catch {
      this.persistent.removeItem(SERVER_STORAGE_KEY);
    }
    return undefined;
  }
}

export async function connectWithWasm(credentials: Credentials): Promise<WasmClient> {
  const wasm = await import("@td/wasm");
  await wasm.default();
  return wasm.TdClient.connect({
    baseUrl: credentials.serverUrl,
    username: credentials.username,
    password: credentials.password,
  });
}
